import React, { useState, useEffect, useMemo } from 'react';
import { Form, DatePicker, Select, Input, Modal, message, TimePicker } from 'antd';
import { LeaveUserCreateDTO, LeaveUserUpdateDTO, LeaveType } from '../../../types/leaves.types';
//import { LEAVE_TYPES } from '../../../constants/leaveConstants';
import leaveService from '../../../services/leave/leave.service';
import dayjs from 'dayjs';
import FileUpload from '../components/upload/FileUpload';
import { FileType } from '../../../types/file.types';
import FileService from '../../../services/file/file.service';
import { handleApiError } from '../../../utils/errorHandler';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n/config';
import enUS from 'antd/lib/calendar/locale/en_US';
import trTR from 'antd/lib/calendar/locale/tr_TR';
import datePickerEn from 'antd/lib/date-picker/locale/en_US';
import datePickerTr from 'antd/lib/date-picker/locale/tr_TR';
const { RangePicker } = DatePicker;
const { TextArea } = Input;
const SEGMENTED_LEAVE_INFO_TEXT =
  "Seçtiğiniz tarih aralığındaki çalışma dışı günler (hafta sonu/resmi tatil) otomatik olarak çıkarıldı. İzin talebiniz yalnızca çalışma günleri için oluşturuldu.";

interface EmployeeLeaveModalProps {
  leaveDataForEdit: LeaveUserUpdateDTO | null;
  onSuccess: () => void;
  open: boolean;
  onCancel: () => void;
}

const EmployeeLeaveModal: React.FC<EmployeeLeaveModalProps> = ({
  leaveDataForEdit,
  onSuccess,
  open,
  onCancel
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedLeaveType, setSelectedLeaveType] = useState<LeaveType | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);



  // 1) dayjs’in locale’ini güncelle
  useEffect(() => {
    dayjs.locale(i18n.language); // örn. 'tr' veya 'en'
  }, [i18n.language]);

  // 3) Sadece DatePicker’ın iç locale’i
  const pickerLocale = useMemo(() => {
    return i18n.language === 'tr' ? datePickerTr : datePickerEn;
  }, [i18n.language]);

  useEffect(() => {
    if (open) {
      if (leaveDataForEdit) {
        form.setFieldsValue({
          ...leaveDataForEdit,
          startDate: leaveDataForEdit.startDate ? dayjs(leaveDataForEdit.startDate) : null,
          endDate: leaveDataForEdit.endDate ? dayjs(leaveDataForEdit.endDate) : null,
          startTime: leaveDataForEdit.startTime ? dayjs(leaveDataForEdit.startTime, 'HH:mm') : null,
          endTime: leaveDataForEdit.endTime ? dayjs(leaveDataForEdit.endTime, 'HH:mm') : null,
        });
        setSelectedLeaveType(leaveDataForEdit.type);
      } else {
        form.resetFields();
        setSelectedLeaveType(LeaveType.ANNUAL);
      }
    }
  }, [open, leaveDataForEdit, form]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      
      // Önce dosyayı yükleyelim (eğer varsa)
      let documentUrl = undefined;
      if (selectedFile) {
        try {
          const fileType = FileType.LEAVE;
          const uploadResponse = await FileService.getInstance().uploadFile(selectedFile, fileType);
          if (uploadResponse.filePath.path && uploadResponse.filePath) {
            documentUrl = uploadResponse.filePath.path;
            //console.log('Dosya yükleme başarılı:', documentUrl);
          } else {
            //console.error('Dosya yükleme yanıtı geçersiz');
            message.error(t('common.error.fileUploadError'));
            setLoading(false);
            return;
          }
        } catch (error: any) {
          handleApiError(error,t);
          setLoading(false);
          return;
        }
      }
      
      let startDate, endDate;
      let [startTime, endTime] = values.type === 'hourly' ? values.timeRange : [undefined, undefined];
      if (values.type === 'hourly') {
        const date = values.date;
        // Tarih: YYYY-MM-DD HH:mm:ss
        startDate = date
          .hour(startTime.hour())
          .minute(startTime.minute())
          .second(0)
          .format('YYYY-MM-DD HH:mm:ss');
      
        endDate = date
          .hour(endTime.hour())
          .minute(endTime.minute())
          .second(0)
          .format('YYYY-MM-DD HH:mm:ss');
      
        // Saatler: HH:mm
        startTime = startTime.format('HH:mm');
        endTime = endTime.format('HH:mm');
      }
      else if (values.type === 'casual') {
        // Casual (mazeret) izni için tek tarih seçilir
        startDate = values.dateRange;
        endDate = values.dateRange;
        // YYYY-MM-DD formatı kullanarak timezone kaymasını önlüyoruz
        startDate = startDate.format('YYYY-MM-DD');
        endDate = endDate.format('YYYY-MM-DD');  
      }
      else {
        // Normal izinler için tarih aralığı
        [startDate, endDate] = values.dateRange;
        // toISOString() UTC'ye çevirdiği için 1 gün geri kayma oluşuyor
        // YYYY-MM-DD formatı kullanarak bu sorunu çözüyoruz
        startDate = startDate.format('YYYY-MM-DD');
        endDate = endDate.format('YYYY-MM-DD');
      }

      const leaveData: LeaveUserCreateDTO = {
        type: values.type,
        startDate,
        endDate,
        startTime: values.type === 'hourly' ? startTime : undefined,
        endTime: values.type === 'hourly' ? endTime : undefined,
        reason: values.reason,
        documentUrl: documentUrl
      };

      
      //console.log('Gönderilecek izin verisi:', leaveData);

      if (leaveDataForEdit?.id) {
        await leaveService.updateLeaveByUser(leaveDataForEdit.id, leaveData as LeaveUserUpdateDTO);
        message.success(t('common.success.leaveUpdateSuccess'));
      } else {
        const createResponse = await leaveService.createLeave(leaveData);
        if (createResponse?.segmentedByWorkingDays) {
          Modal.info({
            title: "Bilgilendirme",
            content: SEGMENTED_LEAVE_INFO_TEXT,
            okText: "Tamam",
            width: 480,
            centered: true,
          });
        } else {
          message.success(t('common.success.leaveCreateSuccess'));
        }
      }

      onSuccess();
      form.resetFields();
      setSelectedFile(null);
    } catch (error: any) {
      handleApiError(error,t);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleLeaveTypeChange = (value: LeaveType) => {
    setSelectedLeaveType(value);
    form.setFieldsValue({
      dateRange: undefined,
      date: undefined,
      timeRange: undefined
    });
  };

  const handleFileChange = (file: File | null) => {
    setSelectedFile(file);
  };

  return (
    <Modal
      title={leaveDataForEdit ? t('common.buttons.editLeaveRequest') : t('common.buttons.createLeaveRequest')}
      open={open}
      onCancel={handleCancel}
      maskClosable={true}
      footer={[
        <button
          key="cancel"
          type="button"
          className="btn btn-light"
          onClick={handleCancel}
          disabled={loading}
        >
          {t('common.buttons.cancelLeaveRequest')}
        </button>,
        <button
          key="submit"
          type="button"
          className="btn btn-primary ms-2"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-1" />
              {t('common.buttons.savingLeaveRequest')}
            </>
          ) : t('common.buttons.saveLeaveRequest')}
        </button>
      ]}
      width={600}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={leaveDataForEdit ? {
          type: leaveDataForEdit.type,
          dateRange: leaveDataForEdit.type === 'hourly' 
            ? undefined 
            : [dayjs(leaveDataForEdit.startDate), dayjs(leaveDataForEdit.endDate)],
          date: leaveDataForEdit.type === 'hourly' ? dayjs(leaveDataForEdit.startDate) : undefined,
          timeRange: leaveDataForEdit.type === 'hourly' 
            ? [dayjs(leaveDataForEdit.startTime, 'HH:mm'), dayjs(leaveDataForEdit.endTime, 'HH:mm')] 
            : undefined,
          reason: leaveDataForEdit.reason
        } : undefined}
      >
        <Form.Item
          name="type"
          label={t('common.leaveType.leaveTypeLabel')}
          rules={[{ required: true, message: t('common.error.leaveTypeRequired') }]}
        >
          <Select 
            placeholder={t('common.leaveType.leaveTypeRequired')}
            onChange={handleLeaveTypeChange}
          >
            {Object.entries(LeaveType).map(([value, label]) => (
              <Select.Option key={value} value={label}>
                {t(`common.leaveType.${label}`)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {selectedLeaveType === 'hourly' ? (
          <>
            <Form.Item
              name="date"
              label={t('common.date.dateLabel')}
              rules={[{ required: true, message: t('common.error.dateRequired') }]}
            >
              <DatePicker 
                className="w-100"
                format="DD.MM.YYYY"
                placeholder={t('common.date.dateRequired')}
                disabledDate={(current) => current && current < dayjs().startOf('day')}
                locale={pickerLocale}
              />
            </Form.Item>

            <Form.Item
              name="timeRange"
              label={t('common.timeRange.timeRangeLabel')}
              rules={[{ required: true, message: t('common.error.timeRangeRequired') }]}
            >
              <TimePicker.RangePicker 
                className="w-100"
                format="HH:mm"
                minuteStep={30}
                locale={pickerLocale}
              />
            </Form.Item>
          </>
        ) 
        // : selectedLeaveType === 'casual' ? 
        // (
        //   <Form.Item
        //     name="dateRange"
        //     label={t('common.dateRange.dateRangeLabel')}
        //     rules={[{ required: true, message: t('common.error.dateRangeRequired') }]}
        //   >
        //     <DatePicker
        //       className="w-100"
        //       format="DD.MM.YYYY"
        //       placeholder={t('common.date.dateRequired')}
        //       disabledDate={(current) => current && current < dayjs().startOf('day')}
        //       locale={pickerLocale}
        //     />
        //   </Form.Item>
        //  ) 
        : (
          <Form.Item
            name="dateRange"
            label={t('common.dateRange.dateRangeLabel')}
            rules={[{ required: true, message: t('common.error.dateRangeRequired') }]}
          >
            <RangePicker 
              className="w-100"
              format="DD.MM.YYYY"
              placeholder={[t('common.dateRange.dateRangePlaceholderStart'), t('common.dateRange.dateRangePlaceholderEnd')]}
              disabledDate={(current) => current && current < dayjs().startOf('day')}
              locale={pickerLocale}
            />
          </Form.Item>
        )}

        <Form.Item
          name="reason"
          label={t('common.reason.reasonLabel')}
          rules={[
            { required: true, message: t('common.error.reasonRequired') },
            { min: 10, message: t('common.error.reasonMinLength', { min: 10 }) }
          ]}
        >
          <TextArea 
            rows={4} 
            placeholder={t('common.reason.reasonRequired')}
            showCount
            maxLength={500}
          />
        </Form.Item>

        <Form.Item
          name="document"
          label={t('common.document.documentLabel')}
          extra={t('common.document.documentExtra')}
        >
          <FileUpload
            fileType={FileType.LEAVE}
            // fileType={FileType.OTHER}
            onChange={handleFileChange}
            maxCount={1}
            buttonText={t('common.document.documentButtonText')}
            accept=".pdf,.jpg,.jpeg,.png"
            disabled={loading}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EmployeeLeaveModal;